import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Scale, AlertTriangle, ArrowUpDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { mpApi, initials, ProgressBar, clampPct, monthLabel } from "./mpShared";

const SORTS = [["highest", "Highest workload"], ["lowest", "Lowest workload"], ["pending", "Most pending"], ["overdue", "Most overdue"], ["name", "Name A–Z"]];
const CAP_KEY = "mp_capacity_per_person";

export default function WorkloadTab({ month, businessId, canManage, clients, refresh, setFilter }) {
  const [data, setData] = useState(null);
  const [sort, setSort] = useState("highest");
  const [capacity, setCapacity] = useState(() => { try { return Number(localStorage.getItem(CAP_KEY)) || 25; } catch (_) { return 25; } });
  const [showBalance, setShowBalance] = useState(false);
  const [hideIdle, setHideIdle] = useState(true);

  const load = useCallback(() => mpApi("/workload", { params: { month, business_id: businessId || undefined } }).then(setData).catch((e) => toast.error(e.message)), [month, businessId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { try { localStorage.setItem(CAP_KEY, String(capacity)); } catch (_) {} }, [capacity]);

  const rows = useMemo(() => {
    if (!data) return [];
    const list = data.members
      .map((m) => {
        const open = m.counts.total - m.counts.completed - m.counts.cancelled;
        const load = capacity > 0 ? Math.round((open / capacity) * 100) : 0;
        return { ...m, open, load, available: Math.max(0, capacity - open) };
      })
      .filter((m) => !hideIdle || m.counts.total > 0);
    const cmp = {
      highest: (a, b) => b.load - a.load, lowest: (a, b) => a.load - b.load,
      pending: (a, b) => b.counts.pending - a.counts.pending, overdue: (a, b) => b.counts.overdue - a.counts.overdue,
      name: (a, b) => a.name.localeCompare(b.name),
    }[sort];
    return list.sort(cmp);
  }, [data, sort, capacity, hideIdle]);
  const overloaded = rows.filter((r) => r.load > 100);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
          <label className="inline-flex items-center gap-1.5">Capacity per person
            <input type="number" min="1" max="200" value={capacity} onChange={(e) => setCapacity(Math.max(1, Number(e.target.value) || 1))}
              className="h-9 w-20 rounded-lg border border-slate-200 px-2 text-sm" /> tasks / month
          </label>
          <label className="inline-flex items-center gap-1.5 text-xs"><input type="checkbox" checked={hideIdle} onChange={(e) => setHideIdle(e.target.checked)} /> Hide people with no tasks</label>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1 text-xs text-slate-500"><ArrowUpDown className="h-3.5 w-3.5" />
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-slate-700">
              {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          {canManage && (
            <button onClick={() => setShowBalance(true)} disabled={!data || data.unassigned.total === 0} data-testid="mp-balance"
              title={data && data.unassigned.total === 0 ? "No unassigned tasks this month" : ""}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
              <Scale className="h-4 w-4" /> Balance Team Workload
            </button>
          )}
        </div>
      </div>

      {data && (data.unassigned.total > 0 || overloaded.length > 0) && (
        <div className="flex flex-wrap gap-2 text-sm">
          {data.unassigned.total > 0 && <span className="rounded-lg bg-amber-50 px-3 py-1.5 text-amber-800 ring-1 ring-amber-200"><b>{data.unassigned.total}</b> task(s) this month are not assigned to anyone.</span>}
          {overloaded.length > 0 && <span className="inline-flex items-center gap-1 rounded-lg bg-rose-50 px-3 py-1.5 text-rose-700 ring-1 ring-rose-200"><AlertTriangle className="h-4 w-4" /> Workload too high: {overloaded.map((o) => o.name).join(", ")}</span>}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {!data ? <div className="py-12 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div>
          : rows.length === 0 ? <div className="py-12 text-center text-sm text-slate-500">No one has tasks in {monthLabel(month)} yet.</div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-sm">
                <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2.5">Team member</th>
                    <th className="px-2 py-2.5 text-right">Assigned</th><th className="px-2 py-2.5 text-right">Done</th>
                    <th className="px-2 py-2.5 text-right">In Prog.</th><th className="px-2 py-2.5 text-right">Pending</th><th className="px-2 py-2.5 text-right">Overdue</th>
                    <th className="w-56 px-3 py-2.5">Workload</th><th className="px-3 py-2.5 text-right">Available</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => (
                    <tr key={m.user_id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2.5">
                        <button onClick={() => setFilter && setFilter("assigned_to", m.user_id)} className="flex items-center gap-2.5 text-left" title="Filter the planner to this person">
                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-50 text-xs font-semibold text-indigo-700">{initials(m.name)}</span>
                          <span><span className="block font-medium text-slate-900">{m.name}</span><span className="block text-xs text-slate-500">{m.designation || m.department || ""}</span></span>
                        </button>
                      </td>
                      <td className="px-2 py-2.5 text-right font-semibold text-slate-900">{m.counts.total}</td>
                      <td className="px-2 py-2.5 text-right text-green-600">{m.counts.completed}</td>
                      <td className="px-2 py-2.5 text-right text-blue-600">{m.counts.in_progress}</td>
                      <td className="px-2 py-2.5 text-right text-amber-600">{m.counts.pending}</td>
                      <td className="px-2 py-2.5 text-right font-medium text-rose-600">{m.counts.overdue || ""}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <ProgressBar pct={clampPct(m.load)} color={m.load > 100 ? "#e11d48" : m.load > 80 ? "#f59e0b" : "#16a34a"} height="h-2.5" />
                          <span className={`w-11 text-right text-xs font-semibold ${m.load > 100 ? "text-rose-600" : "text-slate-700"}`}>{m.load}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right text-slate-700">{m.available} tasks</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
      <p className="text-xs text-slate-400">Workload % = open tasks (not completed) ÷ capacity. Capacity is saved on this browser.</p>

      {showBalance && data && (
        <BalanceDialog month={month} businessId={businessId} members={data.members} clients={clients} unassigned={data.unassigned.total}
          onClose={() => setShowBalance(false)} onDone={() => { load(); refresh(); }} />
      )}
    </div>
  );
}

function BalanceDialog({ month, businessId, members, clients, unassigned, onClose, onDone }) {
  const [picked, setPicked] = useState([]);
  const [clientId, setClientId] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!picked.length) return toast.error("Choose at least one team member");
    setBusy(true);
    try {
      const r = await mpApi("/balance", { method: "POST", body: { month, business_id: businessId || undefined, user_ids: picked, client_id: clientId || undefined } });
      toast.success(`${r.assigned} task(s) assigned`); onDone(); onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl">
        <DialogHeader><DialogTitle>Balance team workload</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Assigns the month's <b>{unassigned}</b> unassigned open task(s) across the people you pick, giving each next task to whoever currently has the fewest open tasks.</p>
        <div>
          <label className="text-xs font-medium text-slate-600">Only for client</label>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm">
            <option value="">All clients</option>
            {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Team members</label>
          <div className="mt-1 flex max-h-56 flex-wrap gap-1.5 overflow-y-auto">
            {members.map((m) => {
              const on = picked.includes(m.user_id);
              return (
                <button key={m.user_id} type="button" onClick={() => setPicked((p) => (on ? p.filter((x) => x !== m.user_id) : [...p, m.user_id]))}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium ring-1 ring-inset ${on ? "bg-indigo-600 text-white ring-indigo-600" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>
                  {m.name} <span className={on ? "text-indigo-100" : "text-slate-400"}>({m.counts.total - m.counts.completed})</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-lg">Cancel</Button>
          <Button onClick={run} disabled={busy} className="rounded-lg bg-emerald-600 text-white hover:bg-emerald-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Assign tasks"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
